import debounce from 'lodash.debounce';

/**
 * Creates a debounced version of an async function that manages multiple pending promises and ensures proper error handling.
 * When multiple calls are made within the wait period, all pending promises will be resolved/rejected with the result/error
 * of the most recent call.
 *
 * @param {Function} func - The async function to debounce.
 * @param {number} wait - The number of milliseconds to delay before calling the function.
 * @returns {Function} A debounced version of the input function that returns a promise which will resolve/reject based on
 *                    the most recent call's result.
 *
 * @example
 * const debouncedSearch = asyncDebounce(async (query) => {
 *   const results = await api.search(query);
 *   return results;
 * }, 300);
 *
 * // Multiple calls within 300ms will all resolve with the results from the last call
 * const promise1 = debouncedSearch('search');
 * const promise2 = debouncedSearch('search term');
 * // Both promises will resolve with results from 'search term'
 *
 * // If the last call throws an error, all pending promises will be rejected
 * try {
 *   await debouncedSearch('error term');
 * } catch (error) {
 *   // Handle error
 * }
 */
export const asyncDebounce = (func, wait) => {
  let currentId = 0;
  const resolveSet = new Set();
  const rejectSet = new Set();

  const debounced = debounce((args, requestId) => {
    func(...args)
      .then((result) => {
        if (requestId === currentId) {
          resolveSet.forEach((resolve) => resolve(result));
          resolveSet.clear();
        }
      })
      .catch((error) => {
        rejectSet.forEach((reject) => reject(error));
        rejectSet.clear();
      });
  }, wait);

  return (...args) => {
    currentId++; // Increment for new request
    return new Promise((resolve, reject) => {
      resolveSet.add(resolve);
      rejectSet.add(reject);
      debounced(args, currentId);
    });
  };
};
